import type { ApiSlaState } from '../schema/tickets/ticket.schema';

/**
 * SKTICK-DB-001 Surface D — the pure logic behind SLA display.
 *
 * Kept free of react-native on purpose: this is the part that decides what a
 * paused clock MEANS, and it must be testable without a renderer. The
 * component in components/SlaState.tsx renders what these return.
 *
 * THE CLIENT COMPUTES NOTHING HERE. `overdue` and `effectiveRemainingSeconds`
 * arrive resolved from the server (SKTICK-CR-001 WP-5), already net of pause
 * including the currently open window. These functions CLASSIFY and FORMAT
 * that state; they never re-derive it. A second calculation would disagree
 * with the escalation sweeper and with SK HQ Console's overdue count, and then
 * a console would contradict the ticket it refers to.
 */

export type SlaVisualState =
  | 'none'
  | 'running'
  | 'approaching'
  | 'paused'
  | 'breached';

/** Inside this window a running clock is shown with warning weight. */
const APPROACHING_SECONDS = 2 * 60 * 60;

/**
 * Classify for display only. Note the order: PAUSED IS CHECKED BEFORE
 * remaining time, because a paused ticket must read as paused even when it is
 * deep into its window — the clock is not running, so "2h left" would be a
 * claim the server is not making.
 *
 * `breached` still wins over `paused`: once the target is missed, saying so is
 * more useful than saying the clock has since been stopped.
 */
export function classifySla(sla: ApiSlaState | undefined): SlaVisualState {
  if (!sla) {
    return 'none';
  }
  if (sla.overdue) {
    return 'breached';
  }
  if (sla.isPaused) {
    return 'paused';
  }
  if (sla.effectiveRemainingSeconds === null) {
    return 'none';
  }
  return sla.effectiveRemainingSeconds <= APPROACHING_SECONDS
    ? 'approaching'
    : 'running';
}

/**
 * Coarse duration — "3d", "4h 10m", "8m". Deliberately not second-precision:
 * an SLA is a promise measured in hours, and a ticking seconds display invites
 * the reader to treat it as live when it is a snapshot from the last fetch.
 */
export function formatDuration(totalSeconds: number): string {
  const s = Math.abs(Math.round(totalSeconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) {
    return h > 0 ? `${d}d ${h}h` : `${d}d`;
  }
  if (h > 0) {
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  if (m > 0) {
    return `${m}m`;
  }
  return 'under a minute';
}

/** The one-line label used in a list row. */
export function slaLabel(sla: ApiSlaState | undefined): string | null {
  const state = classifySla(sla);
  if (state === 'none' || !sla) {
    return null;
  }
  // Copy register: plain, states the fact, no apology and no exclamation.
  if (state === 'paused') {
    return 'Paused — waiting on requester';
  }
  const remaining = sla.effectiveRemainingSeconds;
  if (state === 'breached') {
    return remaining === null
      ? 'Overdue'
      : `Overdue by ${formatDuration(remaining)}`;
  }
  return remaining === null ? null : `${formatDuration(remaining)} left`;
}
